/**
 * The strip under the viewport grid.
 *
 * Every control here acts on the live NiiVue instances through `nvRegistry`
 * rather than on React state, because window/level, drag mode and the histogram
 * are properties of the loaded volume, not of the layout.
 */

import * as React from "react";
import { BarChart3, Contrast, Ruler, Settings2, Sun } from "lucide-react";
import { Button, Checkbox, Select } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";
import { Popover } from "./Popover";
import { listInstances, forEachInstance } from "./nvRegistry";
import { useViewerStore } from "./viewerStore";
import { BASE_COLORMAPS, OVERLAY_COLORMAPS, OVERLAY_COLORMAP_LABELS } from "./types";
import type { CaseNode } from "@/ipc/contract";

const HISTOGRAM_BINS = 48;

/** Voxel histogram of the base volume, read from whichever pane is attached. */
function computeHistogram(): { bins: number[]; min: number; max: number } | null {
  const nv = listInstances()[0];
  const vol = nv?.volumes?.[0];
  const data = vol?.img;
  if (!vol || !data || data.length === 0) return null;

  const min = vol.global_min ?? 0;
  const max = vol.global_max ?? 0;
  if (!(max > min)) return null;

  const bins = new Array<number>(HISTOGRAM_BINS).fill(0);
  // Stride large volumes: a 256³ float volume is 16M samples and this runs on
  // the UI thread. Sampling ~1M voxels is visually indistinguishable.
  const stride = Math.max(1, Math.floor(data.length / 1_000_000));
  for (let i = 0; i < data.length; i += stride) {
    const t = (data[i] - min) / (max - min);
    const bin = Math.min(HISTOGRAM_BINS - 1, Math.max(0, Math.floor(t * HISTOGRAM_BINS)));
    bins[bin] += 1;
  }
  return { bins, min, max };
}

export function ViewerToolbar({ imageNodes, maskNodes }: { imageNodes: CaseNode[]; maskNodes: CaseNode[] }) {
  const store = useViewerStore();
  const [openPanel, setOpenPanel] = React.useState<"window" | "histogram" | "overlay" | null>(null);
  const [measuring, setMeasuring] = React.useState(false);

  const stats = store.baseStats;
  const [level, setLevel] = React.useState<number | null>(null);
  const [windowWidth, setWindowWidth] = React.useState<number | null>(null);

  // Seed the window/level sliders from the volume the first time it loads.
  React.useEffect(() => {
    if (!stats) {
      setLevel(null);
      setWindowWidth(null);
      return;
    }
    setLevel((stats.calMin + stats.calMax) / 2);
    setWindowWidth(Math.max(1e-6, stats.calMax - stats.calMin));
  }, [stats]);

  const applyWindow = React.useCallback((nextLevel: number, nextWidth: number) => {
    forEachInstance((nv) => {
      const vol = nv.volumes[0];
      if (!vol) return;
      vol.cal_min = nextLevel - nextWidth / 2;
      vol.cal_max = nextLevel + nextWidth / 2;
      nv.updateGLVolume();
    });
  }, []);

  const toggleMeasure = React.useCallback(() => {
    setMeasuring((prev) => {
      const next = !prev;
      forEachInstance((nv) => {
        nv.opts.dragMode = next ? nv.dragModes.measurement : nv.dragModes.contrast;
      });
      return next;
    });
  }, []);

  const overlayValue = store.overlayNodeIds[0] ?? "";
  const histogram = openPanel === "histogram" ? computeHistogram() : null;

  return (
    <div className="flex h-10 shrink-0 items-center gap-2 border-t border-ads-border bg-ads-subtle px-2">
      <label className="sr-only" htmlFor="viewer-intensity">
        Intensity colormap
      </label>
      <Select
        id="viewer-intensity"
        className="h-7 w-28 text-xs"
        value={store.baseColormap}
        onChange={(e) => store.setOption("baseColormap", e.target.value)}
        title="Base intensity colormap"
      >
        {BASE_COLORMAPS.map((cm) => (
          <option key={cm} value={cm}>
            {cm === "gray" ? "Intensity" : cm}
          </option>
        ))}
      </Select>

      <Select
        className="h-7 w-36 text-xs"
        value={store.baseNodeId ?? ""}
        onChange={(e) => store.setBaseNode(e.target.value)}
        title="Base volume"
        disabled={imageNodes.length === 0}
      >
        {imageNodes.length === 0 ? <option value="">No volumes</option> : null}
        {imageNodes.map((node) => (
          <option key={node.id} value={node.id}>
            {node.label}
          </option>
        ))}
      </Select>

      <div className="relative">
        <IconButton
          label="Brightness and contrast"
          active={openPanel === "window"}
          onClick={() => setOpenPanel(openPanel === "window" ? null : "window")}
        >
          <Sun className="h-3.5 w-3.5" />
        </IconButton>
        <Popover open={openPanel === "window"} onClose={() => setOpenPanel(null)} className="w-64">
          {stats && level !== null && windowWidth !== null ? (
            <div className="flex flex-col gap-3">
              <SliderRow
                label="Level"
                value={level}
                min={stats.min}
                max={stats.max}
                step={(stats.max - stats.min) / 200 || 1}
                display={level.toFixed(1)}
                onChange={(v) => {
                  setLevel(v);
                  applyWindow(v, windowWidth);
                }}
              />
              <SliderRow
                label="Window"
                value={windowWidth}
                min={(stats.max - stats.min) / 200 || 1}
                max={(stats.max - stats.min) * 1.5 || 1}
                step={(stats.max - stats.min) / 200 || 1}
                display={windowWidth.toFixed(1)}
                onChange={(v) => {
                  setWindowWidth(v);
                  applyWindow(level, v);
                }}
              />
              <Button
                size="sm"
                onClick={() => {
                  forEachInstance((nv) => nv.resetBriCon());
                  setLevel((stats.min + stats.max) / 2);
                  setWindowWidth(stats.max - stats.min);
                }}
              >
                Reset to image defaults
              </Button>
            </div>
          ) : (
            <p className="text-xs text-ads-muted">Load a volume to adjust window and level.</p>
          )}
        </Popover>
      </div>

      <IconButton
        label="Reset window and level"
        onClick={() => forEachInstance((nv) => nv.resetBriCon())}
      >
        <Contrast className="h-3.5 w-3.5" />
      </IconButton>

      <div className="relative">
        <IconButton
          label="Intensity histogram"
          active={openPanel === "histogram"}
          onClick={() => setOpenPanel(openPanel === "histogram" ? null : "histogram")}
        >
          <BarChart3 className="h-3.5 w-3.5" />
        </IconButton>
        <Popover open={openPanel === "histogram"} onClose={() => setOpenPanel(null)} className="w-72">
          <p className="mb-2 text-xs font-semibold text-ads-text">Base volume histogram</p>
          {histogram ? (
            <>
              <div className="flex h-24 items-end gap-px">
                {histogram.bins.map((count, i) => {
                  const peak = Math.max(...histogram.bins) || 1;
                  return (
                    <div
                      key={i}
                      className="flex-1 bg-ads-accent"
                      style={{ height: `${Math.max(1, (count / peak) * 100)}%` }}
                    />
                  );
                })}
              </div>
              <div className="mt-1 flex justify-between text-[10px] text-ads-muted">
                <span>{histogram.min.toFixed(1)}</span>
                <span>{histogram.max.toFixed(1)}</span>
              </div>
            </>
          ) : (
            <p className="text-xs text-ads-muted">No volume loaded.</p>
          )}
        </Popover>
      </div>

      <IconButton label="Measure distance" active={measuring} onClick={toggleMeasure}>
        <Ruler className="h-3.5 w-3.5" />
      </IconButton>

      <label className="ml-2 flex items-center gap-1.5 text-xs text-ads-text">
        <Checkbox
          checked={store.linked}
          onChange={(e) => store.setOption("linked", e.target.checked)}
        />
        Sync Views
      </label>

      <div className="ml-auto flex items-center gap-2">
        <span
          className="h-3 w-3 rounded-sm border border-ads-border-strong"
          style={{ background: overlayValue ? "#e0452c" : "transparent" }}
          aria-hidden
        />
        <Select
          className="h-7 w-44 text-xs"
          value={overlayValue}
          onChange={(e) => {
            const next = e.target.value;
            useViewerStore.setState({ overlayNodeIds: next ? [next] : [] });
          }}
          title="Overlay"
        >
          <option value="">No overlay</option>
          {maskNodes.map((node) => (
            <option key={node.id} value={node.id}>
              {node.label}
            </option>
          ))}
        </Select>

        <div className="relative">
          <IconButton
            label="Overlay settings"
            active={openPanel === "overlay"}
            onClick={() => setOpenPanel(openPanel === "overlay" ? null : "overlay")}
          >
            <Settings2 className="h-3.5 w-3.5" />
          </IconButton>
          <Popover open={openPanel === "overlay"} onClose={() => setOpenPanel(null)} align="right" className="w-56">
            <div className="flex flex-col gap-3">
              <SliderRow
                label="Opacity"
                value={store.overlayOpacity}
                min={0}
                max={1}
                step={0.05}
                display={`${Math.round(store.overlayOpacity * 100)}%`}
                onChange={(v) => store.setOption("overlayOpacity", v)}
              />
              <label className="flex flex-col gap-1 text-xs text-ads-muted">
                Color map
                <Select
                  className="h-7 text-xs"
                  value={store.overlayColormap}
                  onChange={(e) => store.setOption("overlayColormap", e.target.value)}
                >
                  {OVERLAY_COLORMAPS.map((cm) => (
                    <option key={cm} value={cm}>
                      {OVERLAY_COLORMAP_LABELS[cm] ?? cm}
                    </option>
                  ))}
                </Select>
              </label>
            </div>
          </Popover>
        </div>
      </div>
    </div>
  );
}

function IconButton({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "inline-flex h-7 w-7 items-center justify-center rounded border transition-colors",
        active
          ? "border-ads-accent-border bg-ads-accent-soft text-ads-accent"
          : "border-ads-border-strong bg-ads-surface text-ads-muted hover:bg-ads-hover",
      )}
    >
      {children}
    </button>
  );
}

export function SliderRow({
  label,
  value,
  min,
  max,
  step,
  display,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  display: string;
  onChange: (value: number) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-xs text-ads-muted">
      <span className="w-16 shrink-0">{label}</span>
      <input
        type="range"
        className="h-1 flex-1 accent-ads-accent"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <span className="w-12 shrink-0 text-right tabular-nums text-ads-text">{display}</span>
    </label>
  );
}
